import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Toggle from '../../components/ui/Toggle';
import BilingualField from '../../components/ui/BilingualField';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateMoreToComeMutation } from '../../api/account/accountApiSlice';
import type { MoreToComeTexts } from '../../api/account/accountApiTypes';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

interface Props {
    readOnly: boolean;
    /** `tenant.config.moreToCome` — null means the card is off. */
    moreToCome: Partial<Record<'de' | 'en', MoreToComeTexts>> | null;
}

type FieldKey = 'header_text' | 'first_text' | 'second_text';
type FieldValues = Record<FieldKey, { de: string, en: string }>;

const FIELD_KEYS: FieldKey[] = ['header_text', 'first_text', 'second_text'];
const FIELD_LABELS: Array<{ key: FieldKey, label: string }> = [
    { key: 'header_text', label: 'services.idea_card.header' },
    { key: 'first_text', label: 'services.idea_card.first' },
    { key: 'second_text', label: 'services.idea_card.second' },
];

/** One editable unit: the switch and the texts are saved together. */
interface IdeaCardDraft {
    enabled: boolean;
    values: FieldValues;
}

/**
 * Generic default texts, prefilled when a tenant switches the card on without
 * ever having saved texts. Deliberately neutral (they fit any operator);
 * tenant-specific wording — like KGSt Forum's municipal texts — simply
 * replaces them in the fields.
 */
const DEFAULT_TEXTS: Record<'de' | 'en', Record<FieldKey, string>> = {
    de: {
        header_text: 'Unser Angebot wächst',
        first_text: 'Wir arbeiten laufend daran, unser Angebot um weitere Services zu erweitern.',
        second_text: 'Wir sind gespannt auf Ihre Ideen! Schreiben Sie uns direkt hier – oder per E-Mail an {email}.',
    },
    en: {
        header_text: 'More to come',
        first_text: 'We are continuously working on extending our offering with further services.',
        second_text: 'We look forward to your ideas! Write to us right here – or by e-mail at {email}.',
    },
};

/** The save payload's `texts`: the three fields per language, trimmed and
 *  empty-dropped, plus a stored card-label override carried through — it has
 *  no field here (v1) and must not be dropped by a save. Module level so its
 *  branching doesn't count into the component's cognitive complexity. */
const buildTextsPayload = (
    values: FieldValues,
    stored: Partial<Record<'de' | 'en', MoreToComeTexts>> | null,
): Partial<Record<'de' | 'en', MoreToComeTexts>> => {
    const texts: Partial<Record<'de' | 'en', MoreToComeTexts>> = {};
    for (const lang of ['de', 'en'] as const) {
        const entry: MoreToComeTexts = {};
        for (const key of FIELD_KEYS) {
            const text = values[key][lang].trim();
            if (text) entry[key] = text;
        }
        const storedTitle = stored?.[lang]?.alternative_title;
        if (storedTitle) entry.alternative_title = storedTitle;
        if (Object.keys(entry).length > 0) texts[lang] = entry;
    }
    return texts;
};

const initialValues = (
    stored: Partial<Record<'de' | 'en', MoreToComeTexts>> | null,
): FieldValues => {
    const values = {} as FieldValues;
    for (const key of FIELD_KEYS) {
        values[key] = {
            de: stored?.de?.[key] ?? (stored ? '' : DEFAULT_TEXTS.de[key]),
            en: stored?.en?.[key] ?? (stored ? '' : DEFAULT_TEXTS.en[key]),
        };
    }
    return values;
};

/**
 * The end-user "Ideen für Neues" home card — tenant self-service (PO,
 * 2026-09-16). Lives on the Use-cases tab because the card appears on the
 * end-user home screen right next to the use-case tiles this tab configures;
 * when a Services "Experience" tab exists one day (ADR-0005), the section
 * moves there whole.
 *
 * The toggle IS the stored block: on = write `tenant.config.moreToCome`
 * (texts below, generic defaults prefilled), off = remove it. The APP service
 * projects the block into the public config and appends the pseudo-service,
 * so no second flag exists that could disagree.
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern): switch and
 * texts are changed after "Bearbeiten" and saved together.
 */
const IdeaCardSection = ({ readOnly, moreToCome }: Readonly<Props>) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateMoreToCome, { isLoading: saving }] = useUpdateMoreToComeMutation();
    const draft = useEditableDraft<IdeaCardDraft>({
        enabled: moreToCome !== null,
        values: initialValues(moreToCome),
    });
    const { enabled, values } = draft.value;

    const handleFieldChange = (key: FieldKey, lang: 'de' | 'en', value: string) => {
        draft.setDraft({ enabled, values: { ...values, [key]: { ...values[key], [lang]: value } } });
    };

    const handleSave = async () => {
        try {
            if (enabled) {
                await updateMoreToCome({
                    enabled: true,
                    texts: buildTextsPayload(values, moreToCome),
                }).unwrap();
            } else {
                await updateMoreToCome({ enabled: false }).unwrap();
            }
            dispatch(enqueueSuccessSnackbar({
                message: t('services.idea_card.saved'),
                autoHideDuration: 3000,
            }));
            draft.finish();
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t('services.idea_card.error'), t),
                autoHideDuration: 5000,
            }));
        }
    };

    const toggle = (
        <Toggle
            checked={enabled}
            onChange={(next) => draft.setDraft({ enabled: next, values })}
            label={t('services.idea_card.toggle')}
            description={t('services.idea_card.toggle_desc')}
            disabled={!draft.editing || saving}
        />
    );

    const view = (
        <div className={'flex flex-col gap-4'}>
            {toggle}
            {enabled && (
                <dl className={'flex flex-col gap-3 text-sm'}>
                    {FIELD_LABELS.map(({ key, label }) => (
                        <div key={key}>
                            <dt className={'text-foreground/60'}>{t(label)}</dt>
                            <dd className={'text-foreground whitespace-pre-line'}>
                                {values[key].de || t('common.not_applicable')}
                            </dd>
                            <dd className={'text-foreground/60 whitespace-pre-line'}>
                                {values[key].en || t('common.not_applicable')}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
        </div>
    );

    return (
        <EditableSection
            id={'idea-card'}
            title={t('services.idea_card.section')}
            description={t('services.idea_card.section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            onEdit={draft.startEdit}
            onCancel={draft.cancel}
            onSave={() => { void handleSave(); }}
            view={view}
        >
            {toggle}
            {enabled && (
                <div className={'flex flex-col gap-5'}>
                    <BilingualField
                        label={t('services.idea_card.header')}
                        value={values.header_text}
                        onChange={(lang, value) => handleFieldChange('header_text', lang, value)}
                    />
                    <BilingualField
                        label={t('services.idea_card.first')}
                        multiline={true}
                        value={values.first_text}
                        onChange={(lang, value) => handleFieldChange('first_text', lang, value)}
                    />
                    <BilingualField
                        label={t('services.idea_card.second')}
                        multiline={true}
                        hint={t('services.idea_card.email_hint')}
                        value={values.second_text}
                        onChange={(lang, value) => handleFieldChange('second_text', lang, value)}
                    />
                </div>
            )}
        </EditableSection>
    );
};

export default IdeaCardSection;
